import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { AlertCircle, ArrowLeft } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import InstallPrompt from './InstallPrompt';
import { resolveApiUrl } from '../services/api';

interface LoginProps {
  onSwitchToRegister: () => void;
  onBack?: () => void;
}

const Login: React.FC<LoginProps> = ({ onSwitchToRegister, onBack }) => {
  const { t } = useTranslation();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [tokenExpiredMessage, setTokenExpiredMessage] = useState('');
  const [tokenExpiredTitle, setTokenExpiredTitle] = useState('sessionExpired');
  const [requiresTOTP, setRequiresTOTP] = useState(false);
  const [totpCode, setTotpCode] = useState('');
  const { login } = useAuth();

  useEffect(() => {
    const storedMessage = localStorage.getItem('tokenExpiryMessage');
    if (storedMessage) {
      setTokenExpiredMessage(storedMessage);
      setTokenExpiredTitle(localStorage.getItem('tokenExpiryTitle') || 'sessionExpired');
      localStorage.removeItem('tokenExpiryMessage');
      localStorage.removeItem('tokenExpiryTitle');
    }
    const handleTokenExpired = (event: CustomEvent) => {
      setTokenExpiredMessage(event.detail?.message || t('auth.sessionExpiredMessage'));
      setTokenExpiredTitle('sessionExpired');
    };
    window.addEventListener('tokenExpired', handleTokenExpired as EventListener);
    return () => window.removeEventListener('tokenExpired', handleTokenExpired as EventListener);
  }, [t]);

  const handleGoogleLogin = () => {
    window.location.href = resolveApiUrl(`/api/auth/google/oauth?_=${Date.now()}`);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    const result = await login(username, password, requiresTOTP ? totpCode : undefined);
    if (result === 'requires_totp') {
      setRequiresTOTP(true);
    } else if (!result) {
      setError(t('auth.invalidCredentials'));
    }
    setLoading(false);
  };

  return (
    <>
      {onBack && (
        <button
          onClick={onBack}
          style={{
            position: 'fixed',
            top: 20,
            left: 20,
            zIndex: 20,
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            padding: '8px 12px',
            background: 'var(--surface)',
            color: 'var(--ink-muted)',
            border: '1px solid var(--line)',
            borderRadius: 'var(--radius)',
            fontFamily: 'inherit',
            fontSize: 13,
            cursor: 'pointer',
          }}
        >
          <ArrowLeft size={14} /> {t('common.back')}
        </button>
      )}
      <div style={{
        minHeight: '100vh',
        display: 'grid', placeItems: 'center',
        background: 'var(--bg)',
        padding: '40px 20px',
      }}>
        <div style={{
          width: '100%', maxWidth: 420,
          background: 'var(--surface)',
          border: '1px solid var(--line)',
          borderRadius: 'var(--radius-lg)',
          padding: '36px 34px 30px',
          boxShadow: 'var(--shadow)',
        }}>
        <div style={{
          width: 54, height: 54, borderRadius: 12,
          background: 'var(--accent)', color: 'var(--accent-contrast)',
          display: 'grid', placeItems: 'center',
          fontFamily: 'var(--font-serif)', fontStyle: 'italic',
          fontSize: 28, fontWeight: 600, letterSpacing: '-0.5px',
          marginBottom: 20,
        }}>N</div>

        <h1 style={{
          fontFamily: 'var(--font-serif)',
          fontSize: 32, fontWeight: 500,
          letterSpacing: '-0.8px',
          color: 'var(--ink)',
          marginBottom: 6,
        }}>{t('auth.welcomeBack')}</h1>
        <p style={{ color: 'var(--ink-muted)', fontSize: 14, marginBottom: 24 }}>
          {t('auth.signInExisting')}
        </p>

        {tokenExpiredMessage && (
          <div style={{
            display: 'flex', gap: 10,
            background: 'color-mix(in srgb, var(--danger) 10%, transparent)',
            border: '1px solid color-mix(in srgb, var(--danger) 30%, transparent)',
            color: 'var(--danger)',
            borderRadius: 'var(--radius)',
            padding: '10px 12px', fontSize: 12,
            marginBottom: 16,
          }}>
            <AlertCircle size={16} style={{ flexShrink: 0, marginTop: 1 }} />
            <div>
              <div style={{ fontWeight: 600, marginBottom: 2 }}>{t(`auth.${tokenExpiredTitle}`)}</div>
              <div>{tokenExpiredMessage}</div>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div>
            <label htmlFor="username">{t('auth.emailOrUsername')}</label>
            <input
              id="username" type="text" required autoFocus autoComplete="username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder={t('auth.emailOrUsername')}
            />
          </div>
          <div>
            <label htmlFor="password">{t('auth.password')}</label>
            <input
              id="password" type="password" required autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
            />
          </div>

          {requiresTOTP && (
            <div>
              <label htmlFor="totpCode">{t('auth.totpCode', 'Código de autenticação (2FA)')}</label>
              <input
                id="totpCode" type="text" inputMode="numeric" autoFocus autoComplete="one-time-code"
                value={totpCode} onChange={e => setTotpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="000000"
                style={{ letterSpacing: 6, fontFamily: 'var(--font-mono)', fontSize: 20, textAlign: 'center' }}
              />
            </div>
          )}

          {error && (
            <div style={{
              fontSize: 12, color: 'var(--danger)',
              background: 'color-mix(in srgb, var(--danger) 8%, transparent)',
              borderRadius: 'var(--radius-sm)',
              padding: '8px 10px',
            }}>{error}</div>
          )}

          <button
            type="submit" disabled={loading}
            style={{
              marginTop: 4,
              padding: '11px 14px',
              background: 'var(--accent)', color: 'var(--accent-contrast)',
              border: 'none', borderRadius: 'var(--radius)',
              fontFamily: 'inherit', fontSize: 14, fontWeight: 500,
              cursor: loading ? 'not-allowed' : 'pointer',
              opacity: loading ? 0.7 : 1,
            }}
          >
            {loading ? `${t('auth.signIn')}…` : t('auth.signIn')}
          </button>
        </form>

        <div style={{
          display: 'flex', alignItems: 'center', gap: 10,
          margin: '18px 0 14px',
        }}>
          <div style={{ flex: 1, height: 1, background: 'var(--line)' }} />
          <span style={{ fontSize: 12, color: 'var(--ink-faint)' }}>{t('common.or')}</span>
          <div style={{ flex: 1, height: 1, background: 'var(--line)' }} />
        </div>
        <button
          type="button"
          onClick={handleGoogleLogin}
          style={{
            width: '100%', padding: '11px 14px',
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
            background: 'var(--surface)', border: '1px solid var(--line)',
            borderRadius: 'var(--radius)', cursor: 'pointer',
            fontFamily: 'inherit', fontSize: 14, fontWeight: 500, color: 'var(--ink)',
          }}
        >
          <svg width="18" height="18" viewBox="0 0 48 48"><path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/><path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/><path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/><path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/><path fill="none" d="M0 0h48v48H0z"/></svg>
          {t('auth.loginWithGoogle')}
        </button>

        <div style={{
          marginTop: 18, fontSize: 12,
          color: 'var(--ink-faint)', textAlign: 'center',
        }}>
          {t('auth.forgotPassword')} ·{' '}
          <button
            type="button"
            onClick={onSwitchToRegister}
            style={{
              background: 'none', border: 'none', cursor: 'pointer',
              color: 'var(--accent)', fontSize: 12, fontWeight: 500,
              padding: 0, fontFamily: 'inherit',
            }}
          >
            {t('auth.createAccount')}
          </button>
        </div>
      </div>
      </div>
      <InstallPrompt />
    </>
  );
};

export default Login;
